import { cn } from "../../utils/tailwindUtils";

/*
 * Small pieces shared by the lead assignment screens (Settings -> Lead
 * Assignment, the Unassigned queue, the owner history on a lead).
 * Backend: trustedge customers/assignment/.
 */

export const METHOD_LABELS = {
    auto: "Automatic",
    relationship: "Returning owner",
    fallback: "Fallback",
    manual: "Manual",
    bulk: "Bulk",
    distribute: "Distributed",
    claim: "Claimed",
    legacy: "Before tracking",
};

export const METHOD_CLASSES = {
    auto: "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300",
    relationship: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    fallback: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    claim: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
};

export const STATUS_META = {
    available: { label: "Available", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
    at_cap: { label: "At limit", cls: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
    away: { label: "Away", cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
    off: { label: "Not receiving", cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
};

export const inputCls =
    "w-full text-xs rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary";

export const btnPrimary =
    "h-8 px-3 rounded-md text-xs font-medium bg-primary hover:opacity-90 disabled:opacity-40 text-white inline-flex items-center gap-1.5";

export const btnOutline =
    "h-8 px-3 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 inline-flex items-center gap-1.5";

export function Switch({ checked, onChange, disabled, label }) {
    return (
        <button type="button" role="switch" aria-checked={!!checked} aria-label={label} disabled={disabled}
            onClick={() => onChange(!checked)}
            className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors",
                checked ? "bg-primary" : "bg-gray-300 dark:bg-gray-600",
                disabled && "opacity-50 cursor-not-allowed")}>
            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                checked ? "translate-x-6" : "translate-x-1")} />
        </button>
    );
}

export function Field({ label, help, error, children, className }) {
    return (
        <div className={className}>
            {label && <label className="block text-xs font-semibold text-gray-800 dark:text-gray-100 mb-1">{label}</label>}
            {children}
            {help && !error && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{help}</p>}
            {error && <p className="text-[11px] text-red-600 mt-1">{Array.isArray(error) ? error[0] : error}</p>}
        </div>
    );
}

export function Pill({ children, className }) {
    return (
        <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap",
            className || "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300")}>
            {children}
        </span>
    );
}

export function MethodPill({ method }) {
    return <Pill className={METHOD_CLASSES[method]}>{METHOD_LABELS[method] || method}</Pill>;
}

export function Tabs({ tabs, value, onChange }) {
    return (
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            {tabs.map(t => (
                <button key={t.key} type="button" onClick={() => onChange(t.key)}
                    className={cn("px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors",
                        value === t.key
                            ? "border-primary text-primary"
                            : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200")}>
                    {t.label}
                    {t.count != null && <span className="ml-1 text-[10px] text-gray-400">{t.count}</span>}
                </button>
            ))}
        </div>
    );
}

export function Stat({ label, value, hint, tone }) {
    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
            <p className={cn("text-xl font-semibold tabular-nums",
                tone === "warn" ? "text-amber-600" : tone === "bad" ? "text-red-600" : "text-gray-900 dark:text-white")}>
                {value ?? "—"}
            </p>
            {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
        </div>
    );
}

export function fieldErrors(res) {
    return (res && res.errors) || {};
}

export function minutesLabel(m) {
    if (m == null) return "—";
    if (m < 60) return `${Math.round(m)} min`;
    if (m < 1440) return `${Math.floor(m / 60)} h ${Math.round(m % 60)} min`;
    return `${Math.floor(m / 1440)} d`;
}

/*
 * The engine's decision trail, step by step, in words. `decision` is the JSON
 * the backend stores on each LeadAssignmentRequest / returns from simulate.
 */
export function DecisionTrail({ decision }) {
    if (!decision) return null;
    const steps = decision.steps || [];
    const result = decision.result || {};
    return (
        <div className="space-y-2 text-xs">
            <p className="text-gray-500 dark:text-gray-400">
                Trigger: <span className="text-gray-700 dark:text-gray-200">{decision.trigger_label || decision.trigger}</span>
            </p>
            <ol className="space-y-1.5">
                {steps.map((s, i) => <TrailStep key={i} step={s} picked={result.user?.id} />)}
            </ol>
            <div className={cn("rounded-md px-2.5 py-2",
                result.user ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200"
                            : "bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200")}>
                <span className="font-semibold">{result.user ? `→ ${result.user.name}` : "→ Unassigned"}</span>
                {result.summary && <span className="ml-1">{result.summary}</span>}
            </div>
        </div>
    );
}

function TrailStep({ step }) {
    if (step.kind === "rule") {
        return (
            <li className="flex gap-2">
                <span className={cn("mt-0.5 h-2 w-2 rounded-full flex-shrink-0",
                    step.matched ? "bg-emerald-500" : "bg-gray-300")} />
                <span className="text-gray-700 dark:text-gray-200">
                    Rule <b>{step.name}</b>{" "}
                    {step.matched ? "matched" : step.skipped ? <>skipped: {step.skipped}</> : <>did not match: {step.failed}</>}
                </span>
            </li>
        );
    }
    if (step.kind === "existing_owner") {
        return (
            <li className="flex gap-2">
                <span className={cn("mt-0.5 h-2 w-2 rounded-full flex-shrink-0",
                    step.result === "picked" ? "bg-emerald-500" : "bg-gray-300")} />
                <span className="text-gray-700 dark:text-gray-200">
                    Earlier owner {step.user?.name || "—"} (lead #{step.related_lead_id}):{" "}
                    {step.result === "picked" ? "available" : step.reason || "unavailable"}
                </span>
            </li>
        );
    }
    if (step.kind === "pool") {
        const members = step.members || [];
        return (
            <li className="flex gap-2">
                <span className={cn("mt-0.5 h-2 w-2 rounded-full flex-shrink-0",
                    step.picked ? "bg-emerald-500" : "bg-amber-400")} />
                <div className="flex-1 min-w-0">
                    <p className="text-gray-700 dark:text-gray-200">
                        {step.source === "default" ? "Default: " : step.rule_name ? `Rule ${step.rule_name}: ` : ""}
                        <b>{step.team?.name ? `${step.team.name} team` : members[0]?.name || "—"}</b>
                        {step.strategy && step.strategy !== "fixed" && <span className="text-gray-500">, {step.strategy.replace("_", " ")}</span>}
                    </p>
                    {members.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                            {members.map(m => (
                                <span key={m.id} title={m.reason || "Eligible"}
                                    className={cn("px-1.5 py-0.5 rounded text-[10px] border",
                                        m.id === step.picked ? "border-emerald-400 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                                            : m.eligible ? "border-gray-200 text-gray-600 dark:border-gray-600 dark:text-gray-300"
                                                : "border-gray-200 text-gray-400 line-through dark:border-gray-700")}>
                                    {m.name}
                                    {m.daily_cap ? ` · ${m.today}/${m.daily_cap}` : m.today ? ` · ${m.today} today` : ""}
                                    {!m.eligible && m.reason && <span className="no-underline"> ({m.reason})</span>}
                                </span>
                            ))}
                        </div>
                    )}
                    {!members.length && <p className="text-gray-400">No members</p>}
                </div>
            </li>
        );
    }
    if (step.kind === "fallback") {
        const label = step.action === "unassigned" ? "Leave unassigned"
            : step.action === "team_manager" ? "Team manager" : "Fallback person";
        return (
            <li className="flex gap-2">
                <span className={cn("mt-0.5 h-2 w-2 rounded-full flex-shrink-0",
                    step.result === "picked" ? "bg-emerald-500" : "bg-gray-300")} />
                <span className="text-gray-700 dark:text-gray-200">
                    Fallback: {label}{step.user ? ` (${step.user.name})` : ""}
                    {step.result && step.result !== "picked" && step.reason && <> — {step.reason}</>}
                </span>
            </li>
        );
    }
    return null;
}
